import type { Finding } from "@/lib/api/building";

// The Signaalit inspector's day-range chips (7pv / 30pv / 90pv / Kaikki). `null` means
// unbounded ("Kaikki") — no lower bound on either the history fetch or the findings filter.
export type HistoryDaysOption = 7 | 30 | 90 | null;

// A dense series must not be clipped to the same prefix regardless of the selected window
// (bug #1579: 7pv and 30pv painted the same trend because the fetch limit was small enough to
// exhaust itself within the first few hours of any window). Reuses TrendView's HISTORY_LIMIT.
export const HISTORY_FETCH_LIMIT = 20_000;

// Lower bound (ISO, inclusive) for the selected chip, evaluated against `now` so callers can
// pass a fixed instant in tests. `null` ("Kaikki") has no lower bound.
export function historyWindowFromTs(
  days: HistoryDaysOption,
  now: Date,
): string | undefined {
  if (days == null) return undefined;
  return new Date(now.getTime() - days * 86_400_000).toISOString();
}

export type HistoryChartDomain = {
  fromMs: number;
  toMs: number;
};

// Numeric X domain for the inspector chart, pinned to the chip — not to the samples.
// A categorical axis (one equally spaced slot per point) makes 30pv and 90pv look the
// same when the series is shorter than 90 days (e.g. JK-01 run_status starts ~32 days
// ago). Kaikki stays data-driven (undefined).
export function historyChartDomain(
  days: HistoryDaysOption,
  now: Date,
): HistoryChartDomain | undefined {
  const from_ts = historyWindowFromTs(days, now);
  if (from_ts == null) return undefined;
  return { fromMs: Date.parse(from_ts), toMs: now.getTime() };
}

export function historyChartTicks(domain: HistoryChartDomain): number[] {
  return [
    domain.fromMs,
    domain.fromMs + (domain.toMs - domain.fromMs) / 2,
    domain.toMs,
  ];
}

export type HistoryGapBand = { x1: number; x2: number };

// Shade the outage as a band on the relative X axis. Hairline gaps (a few hours
// on a 90-day window) render as a vertical dash — expand to a visible slice of
// the axis and merge overlaps so several nearby breaks stay one readable band.
export function mergeHistoryGapBands(
  gaps: readonly { from: string; to: string }[],
  originMs: number,
  endMs: number,
): HistoryGapBand[] {
  const span = endMs - originMs;
  if (span <= 0 || gaps.length === 0) return [];
  const min_w = span * 0.03;
  const raw = gaps
    .map((g) => {
      let x1 = Date.parse(g.from) - originMs;
      let x2 = Date.parse(g.to) - originMs;
      if (x2 < x1) {
        const swap = x1;
        x1 = x2;
        x2 = swap;
      }
      if (x2 - x1 < min_w) {
        const mid = (x1 + x2) / 2;
        x1 = mid - min_w / 2;
        x2 = mid + min_w / 2;
      }
      return {
        x1: Math.max(0, x1),
        x2: Math.min(span, x2),
      };
    })
    .sort((a, b) => a.x1 - b.x1);

  const merged: HistoryGapBand[] = [];
  for (const band of raw) {
    const last = merged[merged.length - 1];
    if (last && band.x1 <= last.x2) {
      last.x2 = Math.max(last.x2, band.x2);
    } else {
      merged.push({ ...band });
    }
  }
  return merged;
}

// Findings whose opened_at falls within [fromTs, ∞) — shared by the Havainnot list and the
// chart's ReferenceLine markers so switching chips changes both together (bug #1579:
// listBuildingFindings has no time bound, so the full set rendered regardless of chip).
// `fromTs` undefined ("Kaikki") passes every finding through unfiltered.
export function findingsInWindow(
  findings: Finding[],
  fromTs: string | undefined,
): Finding[] {
  if (fromTs == null) return findings;
  return findings.filter((f) => f.opened_at >= fromTs);
}

const INSPECTOR_STATUS_RANK: Record<Finding["status"], number> = {
  open: 2,
  acknowledged: 1,
  resolved: 0,
};

function inspectorFindingRank(finding: Finding): number {
  const openedMs = Date.parse(finding.opened_at);
  const opened = Number.isNaN(openedMs) ? 0 : openedMs;
  return INSPECTOR_STATUS_RANK[finding.status] * 1e15 + opened;
}

/**
 * Signaalit inspector "Näytä havainnot": one card per check, not every quality episode.
 * Datavahtimestari writes a new row when a check fails again, so the same observation
 * would otherwise stack (open + resolved, or several resolved onsets) as duplicate Havainnot.
 * Prefer the live row; otherwise the most recently opened episode still in the window.
 */
export function collapseInspectorFindings(findings: Finding[]): Finding[] {
  const best = new Map<string, Finding>();
  for (const finding of findings) {
    const key = `${finding.source_id}:${finding.finding_type}`;
    const previous = best.get(key);
    if (
      previous == null ||
      inspectorFindingRank(finding) > inspectorFindingRank(previous)
    ) {
      best.set(key, finding);
    }
  }
  return [...best.values()];
}
